<template>
  <div class="ec">
    <!-- Barra superior -->
    <div class="ec__bar">
      <button
        type="button"
        class="ec__bar-back"
        @click="$router.back()"
      >
        <feather-icon icon="ChevronLeftIcon" size="24" />
      </button>
      <span class="ec__bar-title">Nuevo egreso</span>
      <span class="ec__bar-spacer" />
    </div>

    <!-- Partidas ya agregadas a esta compra (visibles) -->
    <div
      v-if="committed.length"
      class="ec__basket"
    >
      <div class="ec__basket-head">
        <feather-icon icon="ShoppingBagIcon" size="15" class="mr-50" />
        <strong>{{ committed.length }}</strong>&nbsp;{{ committed.length === 1 ? 'partida agregada' : 'partidas agregadas' }}
        <span class="ec__basket-sum">${{ committedSum | money }}</span>
      </div>
      <div class="ec__basket-list">
        <div
          v-for="(item, i) in committed"
          :key="i"
          class="ec__basket-row"
        >
          <span class="ec__basket-name">{{ recordLabel(item) }}</span>
          <span class="ec__basket-amt">${{ recordTotal(item) | money }}</span>
          <button
            type="button"
            class="ec__basket-del"
            @click="removeLine(i)"
          >
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- Monto (héroe) -->
    <div class="ec__amount">
      <div class="ec__amount-value">{{ displayAmount }}</div>
      <div class="ec__amount-sub">
        <template v-if="lineSubtotal > 0">
          {{ current.quantity }} × precio · Total con IVA
          <strong>${{ lineTotal | money }}</strong>
        </template>
        <template v-else>
          Toca los números para capturar el precio
        </template>
      </div>
    </div>

    <!-- Campos -->
    <div class="ec__fields">
      <!-- Categoría -->
      <button
        v-b-modal.categorySheet
        type="button"
        class="ec__row"
      >
        <span class="ec__row-icon"><feather-icon icon="GridIcon" size="18" /></span>
        <span class="ec__row-label">Categoría</span>
        <span :class="['ec__row-value', { 'ec__row-value--empty': !categoryLabel }]">
          {{ categoryLabel || 'Elegir' }}
          <feather-icon icon="ChevronRightIcon" size="16" />
        </span>
      </button>

      <!-- Material -->
      <div
        v-if="isMaterial"
        class="ec__material"
      >
        <template v-if="!current.name">
          <vue-bootstrap-typeahead
            v-model="productQuery"
            placeholder="Buscar material por nombre o SKU"
            :data="[...productsResults, ...groupedExpenses]"
            :serializer="item => item.name"
            :show-all-results="true"
            :max-matches="5"
            @hit="selectProduct($event)"
          >
            <template
              slot="suggestion"
              slot-scope="{ data }"
            >
              <div>
                <span class="font-weight-bolder mr-1" style="font-size: 13px">[{{ data.sku }}]</span>
                <span style="font-size: 12px">{{ data.name }}</span>
              </div>
            </template>
          </vue-bootstrap-typeahead>
          <div
            v-if="noMaterialMatches"
            class="ec__not-found"
          >
            <span class="ec__not-found-text">
              {{ $t('materialNotFound', { query: productQuery.trim() }) }}
            </span>
            <b-button
              v-b-modal.newMaterialSheet
              size="sm"
              variant="primary"
              block
              @click="openNewMaterial(productQuery)"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />{{ $t('registerAsNewMaterial') }}
            </b-button>
          </div>
          <button
            v-else
            v-b-modal.newMaterialSheet
            type="button"
            class="ec__link"
            @click="openNewMaterial(productQuery)"
          >
            <feather-icon icon="PlusIcon" size="15" /> Crear material nuevo
          </button>
        </template>
        <div
          v-else
          class="ec__pill"
        >
          <div class="ec__pill-info">
            <span class="ec__pill-name">{{ current.name }}</span>
            <span
              v-if="current.brand || current.sku"
              class="ec__pill-meta"
            >
              <template v-if="current.brand">{{ current.brand }}</template>
              <template v-if="current.brand && current.sku"> · </template>
              <template v-if="current.sku">SKU {{ current.sku }}</template>
            </span>
          </div>
          <button
            type="button"
            class="ec__pill-remove"
            @click="removeProduct"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>
      </div>

      <!-- Cantidad -->
      <div class="ec__row ec__row--static">
        <span class="ec__row-icon"><feather-icon icon="HashIcon" size="18" /></span>
        <span class="ec__row-label">Cantidad</span>
        <div class="ec__stepper">
          <button type="button" class="ec__stepper-btn" @click="decrementQty">
            <feather-icon icon="MinusIcon" size="16" />
          </button>
          <input
            v-model.number="current.quantity"
            type="number"
            inputmode="decimal"
            class="ec__stepper-input"
          >
          <button type="button" class="ec__stepper-btn" @click="incrementQty">
            <feather-icon icon="PlusIcon" size="16" />
          </button>
        </div>
      </div>

      <!-- Proveedor -->
      <div class="ec__row ec__row--static">
        <span class="ec__row-icon"><feather-icon icon="TruckIcon" size="18" /></span>
        <span class="ec__row-label">Proveedor</span>
        <select
          v-model="current.supplier"
          class="ec__select"
        >
          <option
            v-for="opt in supplierOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.text }}
          </option>
        </select>
      </div>
      <input
        v-if="current.supplier === 'Nuevo proveedor'"
        v-model="current.new_supplier"
        placeholder="Nombre del nuevo proveedor"
        class="ec__inline-input"
      >

      <!-- Fecha -->
      <div class="ec__row ec__row--static">
        <span class="ec__row-icon"><feather-icon icon="CalendarIcon" size="18" /></span>
        <span class="ec__row-label">Fecha</span>
        <span class="ec__date-label">{{ dateLabel }} · {{ current.expense_time }}</span>
        <b-form-timepicker
          v-model="current.expense_time"
          class="ec__time"
          locale="es"
          :hour12="false"
          button-only
          right
          size="sm"
          hide-header
          no-close-button
        />
        <b-form-datepicker
          v-model="current.expense_date"
          class="ec__date"
          button-only
          right
          size="sm"
        />
      </div>

      <!-- Estatus -->
      <div class="ec__row ec__row--static">
        <span class="ec__row-icon"><feather-icon icon="CreditCardIcon" size="18" /></span>
        <span class="ec__row-label">Estatus</span>
        <div class="ec__segmented">
          <button
            v-for="opt in statusOptions"
            :key="opt.value"
            type="button"
            :class="['ec__seg', { 'is-active': current.expense_status === opt.value }]"
            @click="current.expense_status = opt.value"
          >
            {{ $t(opt.value) }}
          </button>
        </div>
      </div>

      <!-- Ajustes -->
      <button
        type="button"
        class="ec__row ec__row--toggle"
        @click="showDetails = !showDetails"
      >
        <span class="ec__row-icon"><feather-icon icon="SlidersIcon" size="18" /></span>
        <span class="ec__row-label">Ajustes (IVA, marca, nota)</span>
        <feather-icon
          :icon="showDetails ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          size="18"
          class="text-muted"
        />
      </button>
      <div
        v-if="showDetails"
        class="ec__details"
      >
        <label class="ec__field-label">{{ $t('iva') }} (%)</label>
        <input
          v-model.number="current.iva_percentage"
          type="number"
          inputmode="numeric"
          class="ec__inline-input"
        >
        <label class="ec__field-label">{{ $t('brand') }}</label>
        <input
          v-model="current.brand"
          class="ec__inline-input"
          autocomplete="off"
        >
        <label class="ec__field-label">{{ $t('description') }}</label>
        <input
          v-model="current.description"
          class="ec__inline-input"
          autocomplete="off"
        >
      </div>
    </div>

    <!-- Teclado + acciones (ancla, sobre el menú inferior) -->
    <div class="ec__keypad">
      <div class="ec__keys">
        <button
          v-for="key in keypadKeys"
          :key="key.value"
          type="button"
          class="ec__key"
          @click="pressKey(key.value)"
        >
          <feather-icon
            v-if="key.icon"
            :icon="key.icon"
            size="20"
          />
          <template v-else>{{ key.value }}</template>
        </button>
      </div>
      <div class="ec__actions">
        <b-button
          variant="outline-primary"
          class="ec__add"
          :disabled="!currentValid"
          @click="addAnother"
        >
          <feather-icon icon="PlusIcon" size="16" /> Agregar
        </b-button>
        <b-button
          variant="primary"
          class="ec__save"
          :disabled="!canSave || saving"
          @click="submit"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          <template v-else>
            {{ $t('save') }}<span v-if="saveCount > 1"> ({{ saveCount }})</span> · ${{ grandTotal | money }}
          </template>
        </b-button>
      </div>
    </div>

    <!-- Hoja: categorías -->
    <b-modal
      id="categorySheet"
      ok-only
      :ok-title="$t('continue')"
      centered
      dialog-class="ec-sheet__dialog"
      content-class="ec-sheet__content"
      title="¿En qué gastaste?"
    >
      <expense-type-menu
        :index="0"
        :expense-type-catalogs="expenseTypeCatalogs"
        :record="current"
        :history="history"
        :allow-manage="false"
        @refetch="$emit('refetch-categories')"
      />
    </b-modal>

    <!-- Hoja: material nuevo -->
    <b-modal
      id="newMaterialSheet"
      :title="$t('newMaterial')"
      :ok-title="$t('save')"
      :cancel-title="$t('cancel')"
      :ok-disabled="!newMaterialValid"
      centered
      @ok="selectNewMaterial"
      @cancel="newMaterial = {}"
    >
      <b-alert
        show
        variant="info"
        class="p-75 small"
      >
        {{ $t('expenseCapture.newMaterialNote') }}
      </b-alert>
      <b-form-group :label="$t('name')">
        <b-form-input v-model="newMaterial.name" autocomplete="off" />
      </b-form-group>
      <b-form-group :label="$t('unit')">
        <b-form-select
          v-model="newMaterial.unit_type"
          :options="unitOptions"
        />
        <small class="text-muted">{{ $t('expenseCapture.unitHint') }}</small>
      </b-form-group>
      <b-form-group :label="$t('brand')">
        <b-form-input v-model="newMaterial.brand" autocomplete="off" />
      </b-form-group>
      <b-form-group :label="$t('description')" class="mb-0">
        <b-form-input v-model="newMaterial.description" autocomplete="off" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'underscore'
import { todayLocalDate, nowLocalTime, expenseDateForApi } from '@/utils/expenseDates'
import ExpenseTypeMenu from './ExpenseTypeMenu.vue'

export default {
  components: {
    ExpenseTypeMenu,
  },
  props: {
    expenseTypeCatalogs: {
      type: Array,
      default: () => ([]),
    },
    materialCatalogId: {
      type: [String, Number],
      default: null,
    },
    supplierOptions: {
      type: Array,
      default: () => ([]),
    },
    groupedExpenses: {
      type: Array,
      default: () => ([]),
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      records: [],
      history: [],
      amountStr: '',
      productQuery: '',
      productsResults: [],
      searchingProducts: false,
      newMaterial: {},
      showDetails: false,
      statusOptions: [
        { value: 'paid' },
        { value: 'credit' },
      ],
      keypadKeys: [
        { value: '1' }, { value: '2' }, { value: '3' },
        { value: '4' }, { value: '5' }, { value: '6' },
        { value: '7' }, { value: '8' }, { value: '9' },
        { value: '.' }, { value: '0' }, { value: 'del', icon: 'DeleteIcon' },
      ],
    }
  },
  computed: {
    unitOptions() {
      const keys = ['piece', 'bundle', 'box', 'kilogram', 'ton', 'meter', 'square_meters', 'cubic_meters', 'trip']
      return [
        { value: null, text: this.$t('expenseCapture.pickUnit') },
        ...keys.map(value => ({ value, text: this.$tc(this.unitsFilter(value), 2) })),
      ]
    },
    newMaterialValid() {
      return Boolean((this.newMaterial.name || '').trim()) && Boolean(this.newMaterial.unit_type)
    },

    noMaterialMatches() {
      const query = this.productQuery.trim().toLowerCase()
      if (query.length < 2 || this.searchingProducts) return false

      const matches = item => String(item?.name || '').toLowerCase().includes(query)
        || String(item?.sku || '').toLowerCase().includes(query)

      return !this.productsResults.some(matches) && !this.groupedExpenses.some(matches)
    },

    ...mapGetters('app', ['unitsFilter']),
    current() {
      return this.records[this.records.length - 1] || {}
    },
    committed() {
      return this.records.slice(0, -1)
    },
    isMaterial() {
      return this.materialCatalogId
        && this.current.expense_type_catalog_id === this.materialCatalogId
    },
    categoryLabel() {
      return this.history.map(category => category.name).join(' / ')
    },
    lineSubtotal() {
      return (Number(this.current.unit_price) || 0) * (Number(this.current.quantity) || 0)
    },
    ivaAmount() {
      return this.lineSubtotal * ((Number(this.current.iva_percentage) || 0) / 100)
    },
    lineTotal() {
      return this.lineSubtotal + this.ivaAmount
    },
    displayAmount() {
      if (!this.amountStr) return '$0'
      const [intPart, decPart] = this.amountStr.split('.')
      const intFmt = Number(intPart || '0').toLocaleString('es-MX')
      if (this.amountStr.includes('.')) return `$${intFmt}.${decPart}`
      return `$${intFmt}`
    },
    dateLabel() {
      if (!this.current.expense_date) return ''
      const [y, m, d] = this.current.expense_date.split('-')
      return `${d}/${m}/${y.slice(2)}`
    },
    currentValid() {
      const hasCategory = !!this.current.expense_type_catalog_id
      const hasMaterial = !this.isMaterial || !!this.current.name
      const hasAmount = (Number(this.current.unit_price) || 0) > 0
        && (Number(this.current.quantity) || 0) > 0
      const needsNewSupplier = this.current.supplier === 'Nuevo proveedor'
      const hasSupplier = !!this.current.supplier
        && (!needsNewSupplier || !!(this.current.new_supplier || '').trim())
      return hasCategory && hasMaterial && hasAmount && hasSupplier
    },
    committedSum() {
      return this.committed.reduce((sum, record) => sum + this.recordTotal(record), 0)
    },
    grandTotal() {
      return this.committedSum + (this.currentValid ? this.lineTotal : 0)
    },
    saveCount() {
      return this.committed.length + (this.currentValid ? 1 : 0)
    },
    canSave() {
      return this.currentValid || this.committed.length > 0
    },
  },
  watch: {
    productQuery(query) {
      this.searchingProducts = query.trim().length >= 2
      this.searchProducts(query)
    },
  },
  created() {
    this.records = [this.blankRecord()]
  },
  methods: {
    ...mapActions('products', ['fetchProducts']),
    searchProducts: _.debounce(function search(query) {
      if (!query.trim().length) {
        this.searchingProducts = false
        return
      }
      this.fetchProducts({
        by_name: query,
        by_active_status: true,
        meta: { pagination: { per_page: 10 } },
      }).then(response => {
        this.productsResults = JSON.parse(JSON.stringify(response.data))
      }).finally(() => {
        this.searchingProducts = false
      })
    }, 500),
    openNewMaterial(name = '') {
      this.newMaterial = {
        name: name.trim(), unit_type: null, brand: '', description: '',
      }
    },
    blankRecord(carry = {}) {
      return {
        expense_type_catalog_id: '',
        name: '',
        quantity: 1,
        unit_price: 0,
        iva_percentage: 16,
        supplier: carry.supplier || 'YoConstruyo',
        brand: '',
        description: '',
        sku: '',
        expense_status: carry.expense_status || 'paid',
        expense_date: carry.expense_date || todayLocalDate(),
        expense_time: carry.expense_time || nowLocalTime(),
        expense_create_type: this.$route.query.createType,
        new_supplier: '',
        unit_type: '',
        material_suggestion: false,
      }
    },
    recordTotal(record) {
      const subtotal = (Number(record.unit_price) || 0) * (Number(record.quantity) || 0)
      return subtotal * (1 + (Number(record.iva_percentage) || 0) / 100)
    },
    recordLabel(record) {
      return record.name || this.$t('expense')
    },
    pressKey(value) {
      if (value === 'del') {
        this.amountStr = this.amountStr.slice(0, -1)
      } else if (value === '.') {
        if (!this.amountStr.includes('.')) {
          this.amountStr = this.amountStr === '' ? '0.' : `${this.amountStr}.`
        }
      } else {
        if (this.amountStr.includes('.') && this.amountStr.split('.')[1].length >= 2) return
        if (this.amountStr.replace('.', '').length >= 10) return
        this.amountStr = this.amountStr === '0' ? value : this.amountStr + value
      }
      this.current.unit_price = Number(this.amountStr) || 0
    },
    incrementQty() {
      this.current.quantity = Math.round(((Number(this.current.quantity) || 0) + 1) * 100) / 100
    },
    decrementQty() {
      const next = (Number(this.current.quantity) || 0) - 1
      this.current.quantity = next > 0 ? Math.round(next * 100) / 100 : 0
    },
    selectProduct(product) {
      this.current.name = product.name
      this.current.brand = product.brand
      this.current.description = product.description
      this.current.sku = product.sku
      this.current.unit_type = product.unit_type
      this.current.material_suggestion = false
      if (Number(product.quantity) > 0) {
        this.current.quantity = product.quantity
      }
    },
    removeProduct() {
      this.current.name = ''
      this.current.brand = ''
      this.current.description = ''
      this.current.sku = ''
      this.current.unit_type = ''
      this.productQuery = ''
    },
    selectNewMaterial() {
      this.current.name = (this.newMaterial.name || '').trim()
      this.current.brand = this.newMaterial.brand || ''
      this.current.description = this.newMaterial.description || ''
      this.current.unit_type = this.newMaterial.unit_type || ''
      this.current.sku = ''
      this.current.material_suggestion = true
      this.newMaterial = {}
    },
    // Aplica la misma normalización del payload que el formulario de escritorio.
    finalizeRecord(record) {
      record.expense_date = expenseDateForApi(record.expense_date, record.expense_time)
      if (record.supplier === 'Nuevo proveedor') {
        record.supplier = (record.new_supplier || '').trim()
      }
      if (this.materialCatalogId && record.expense_type_catalog_id !== this.materialCatalogId) {
        const lastCat = this.history[this.history.length - 1]
        if (lastCat) record.name = lastCat.name
      }
    },
    addAnother() {
      if (!this.currentValid) return
      this.finalizeRecord(this.current)
      const carry = {
        supplier: this.current.supplier,
        expense_date: this.current.expense_date,
        expense_time: this.current.expense_time,
        expense_status: this.current.expense_status,
      }
      this.records.push(this.blankRecord(carry))
      this.history.splice(0)
      this.amountStr = ''
      this.productQuery = ''
      this.showDetails = false
    },
    removeLine(index) {
      this.records.splice(index, 1)
    },
    submit() {
      if (!this.canSave || this.saving) return
      const records = this.records.slice()
      if (this.currentValid) {
        this.finalizeRecord(this.current)
      } else {
        records.pop()
      }
      if (!records.length) return
      this.$emit('save', records)
    },
  },
}
</script>

<style lang="scss" scoped>
$ec-primary: #340668;
$ec-line: #eceaf1;
$ec-muted: #6b7280;
$ec-brand-soft: #f3f0fb;
$ec-nav: 65px;

.ec {
  background: #fff;
  display: flex;
  flex-direction: column;
  padding-bottom: 300px;
}

/* Barra superior */
.ec__bar {
  align-items: center;
  display: flex;
  height: 52px;
  padding: 0 0.5rem;
}

.ec__bar-back {
  background: transparent;
  border: 0;
  color: $ec-primary;
  padding: 0.25rem;
}

.ec__bar-title {
  flex: 1;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.ec__bar-spacer {
  width: 32px;
}

/* Partidas agregadas (carrito visible) */
.ec__basket {
  background: $ec-brand-soft;
  border: 1px solid #e6e0f4;
  border-radius: 12px;
  margin: 0.25rem 1rem 0.5rem;
  overflow: hidden;
}

.ec__basket-head {
  align-items: center;
  color: $ec-primary;
  display: flex;
  font-size: 0.85rem;
  padding: 0.6rem 0.75rem;

  strong {
    font-weight: 800;
  }
}

.ec__basket-sum {
  font-weight: 800;
  margin-left: auto;
}

.ec__basket-list {
  background: #fff;
  max-height: 132px;
  overflow-y: auto;
}

.ec__basket-row {
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 0.55rem 0.75rem;

  & + & {
    border-top: 1px solid $ec-line;
  }
}

.ec__basket-name {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ec__basket-amt {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ec__basket-del {
  background: transparent;
  border: 0;
  color: #b42318;
  display: inline-flex;
  padding: 0 0 0 0.25rem;
}

/* Monto héroe */
.ec__amount {
  padding: 1rem 1rem 1.5rem;
  text-align: center;
}

.ec__amount-value {
  color: #111827;
  font-size: 3rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.ec__amount-sub {
  color: $ec-muted;
  font-size: 0.82rem;
  margin-top: 0.35rem;

  strong {
    color: #111827;
  }
}

/* Filas tipo lista */
.ec__fields {
  border-top: 1px solid $ec-line;
}

.ec__row {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 1px solid $ec-line;
  display: flex;
  gap: 0.75rem;
  min-height: 56px;
  padding: 0.5rem 1rem;
  text-align: left;
  width: 100%;
}

.ec__row-icon {
  align-items: center;
  color: $ec-primary;
  display: flex;
  justify-content: center;
  width: 22px;
}

.ec__row-label {
  color: #111827;
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
}

.ec__row-value {
  align-items: center;
  color: #111827;
  display: flex;
  font-weight: 600;
  gap: 0.15rem;
  max-width: 55%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--empty {
    color: $ec-primary;
  }
}

.ec__row--toggle .ec__row-label {
  color: $ec-muted;
  font-weight: 500;
}

/* Cantidad */
.ec__stepper {
  align-items: center;
  display: flex;
  gap: 0.4rem;
}

.ec__stepper-btn {
  align-items: center;
  background: #f5f4f8;
  border: 0;
  border-radius: 0.55rem;
  color: $ec-primary;
  display: flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.ec__stepper-input {
  border: 1px solid $ec-line;
  border-radius: 0.55rem;
  font-size: 1rem;
  font-weight: 700;
  height: 38px;
  text-align: center;
  width: 62px;
}

/* Proveedor / select nativo */
.ec__select {
  background: transparent;
  border: 0;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 600;
  max-width: 60%;
  text-align: right;
  text-align-last: right;
}

.ec__inline-input {
  border: 1px solid $ec-line;
  border-radius: 0.6rem;
  display: block;
  font-size: 0.95rem;
  margin: 0 1rem 0.75rem;
  padding: 0.6rem 0.75rem;
  width: calc(100% - 2rem);
}

.ec__field-label {
  color: $ec-muted;
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0.25rem 1rem 0.35rem;
}

/* Fecha */
.ec__time {
  margin-right: 0.25rem;
}

.ec__date-label {
  color: #111827;
  font-weight: 600;
}

/* Estatus segmentado */
.ec__segmented {
  background: #f5f4f8;
  border-radius: 0.6rem;
  display: flex;
  gap: 0.2rem;
  padding: 0.2rem;
}

.ec__seg {
  background: transparent;
  border: 0;
  border-radius: 0.45rem;
  color: $ec-muted;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.35rem 0.9rem;

  &.is-active {
    background: #fff;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
    color: $ec-primary;
  }
}

/* Material */
.ec__material {
  border-bottom: 1px solid $ec-line;
  padding: 0.75rem 1rem;
}

.ec__not-found {
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  border: 1px dashed #c9c4d6;
  border-radius: 0.5rem;
  background: #faf9fc;
}

.ec__not-found-text {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.82rem;
  color: #5e5a6e;
}

.ec__link {
  background: transparent;
  border: 0;
  color: $ec-primary;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding: 0;
}

.ec__pill {
  align-items: center;
  background: #f5f4f8;
  border-radius: 0.6rem;
  display: flex;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
}

.ec__pill-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ec__pill-name {
  font-weight: 700;
}

.ec__pill-meta {
  color: $ec-muted;
  font-size: 0.78rem;
}

.ec__pill-remove {
  background: transparent;
  border: 0;
  color: #b42318;
  padding-left: 0.5rem;
}

.ec__details {
  border-bottom: 1px solid $ec-line;
  padding-bottom: 0.5rem;
}

/* Teclado + acciones (ancla, siempre sobre el menú inferior de 65px) */
.ec__keypad {
  background: #fff;
  border-top: 1px solid $ec-line;
  bottom: $ec-nav;
  box-shadow: 0 -6px 20px rgba(16, 24, 40, 0.08);
  left: 0;
  padding: 0.5rem 0.75rem calc(0.5rem + env(safe-area-inset-bottom));
  position: fixed;
  right: 0;
  z-index: 10;
}

.ec__keys {
  display: grid;
  gap: 0.35rem;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 0.5rem;
}

.ec__key {
  align-items: center;
  background: #f5f4f8;
  border: 0;
  border-radius: 0.7rem;
  color: #111827;
  display: flex;
  font-size: 1.35rem;
  font-weight: 700;
  height: 44px;
  justify-content: center;

  &:active {
    background: #e7e4ef;
  }
}

.ec__actions {
  display: flex;
  gap: 0.5rem;
}

.ec__add {
  align-items: center;
  display: flex;
  flex: 0 0 40%;
  font-weight: 700;
  height: 50px;
  justify-content: center;
}

.ec__save {
  align-items: center;
  display: flex;
  flex: 1;
  font-size: 1rem;
  font-weight: 700;
  height: 50px;
  justify-content: center;
}
</style>

<style>
/* Hojas inferiores (los modales se montan en body, fuera del scope) */
.ec-sheet__dialog {
  bottom: 0;
  left: 0;
  margin: 0;
  max-width: 100%;
  position: fixed;
  right: 0;
}

.ec-sheet__content {
  border-radius: 18px 18px 0 0;
  max-height: 88vh;
  overflow-y: auto;
}
</style>
